import React, { useState } from 'react';
import { Send, Zap, Power, Settings } from 'lucide-react';
import { apiService } from '../services/apiService';

function OrdenesPanel({ plantaId }) {
  const [tipoOrden, setTipoOrden] = useState('consigna_potencia');
  const [potencia, setPotencia] = useState('');
  const [operador, setOperador] = useState('');
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const handleEnviarOrden = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMensaje(null);

    try {
      let parametros = {};
      
      switch (tipoOrden) {
        case 'consigna_potencia':
          parametros = { potencia: parseFloat(potencia) };
          break;
        case 'arranque':
        case 'parada':
          parametros = {};
          break;
        default:
          parametros = {};
      }

      await apiService.enviarOrden(plantaId, tipoOrden, parametros, operador || 'Dashboard');
      
      setMensaje({ tipo: 'success', texto: 'Orden enviada exitosamente' });
      setPotencia('');
      
    } catch (error) {
      setMensaje({ tipo: 'error', texto: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Enviar Órdenes</h2>
        <p className="text-gray-600">
          Envía comandos de control a la planta seleccionada
        </p>
      </div>

      <div className="card">
        <form onSubmit={handleEnviarOrden} className="space-y-6">
          {/* Tipo de Orden */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Orden
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OrdenButton
                active={tipoOrden === 'consigna_potencia'}
                onClick={() => setTipoOrden('consigna_potencia')}
                icon={Zap}
                title="Consigna de Potencia"
                description="Establecer potencia objetivo"
              />
              <OrdenButton
                active={tipoOrden === 'arranque'}
                onClick={() => setTipoOrden('arranque')}
                icon={Power}
                title="Arranque"
                description="Arrancar la planta"
              />
              <OrdenButton
                active={tipoOrden === 'parada'}
                onClick={() => setTipoOrden('parada')}
                icon={Power}
                title="Parada"
                description="Detener la planta"
              />
            </div>
          </div>

          {/* Parámetros según tipo de orden */}
          {tipoOrden === 'consigna_potencia' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Potencia Objetivo (kW)
              </label>
              <input
                type="number"
                value={potencia}
                onChange={(e) => setPotencia(e.target.value)}
                className="input"
                placeholder="Ej: 1500"
                min="0"
                max="2000"
                step="0.01"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Rango válido: 0 - 2000 kW
              </p>
            </div>
          )}

          {/* Operador */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Operador (Opcional)
            </label>
            <input
              type="text"
              value={operador}
              onChange={(e) => setOperador(e.target.value)}
              className="input"
              placeholder="Nombre del operador"
            />
          </div>

          {/* Mensaje de resultado */}
          {mensaje && (
            <div className={`p-4 rounded-lg ${
              mensaje.tipo === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {mensaje.texto}
            </div>
          )}

          {/* Botón enviar */}
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full flex items-center justify-center space-x-2"
          >
            <Send className="w-5 h-5" />
            <span>{loading ? 'Enviando...' : 'Enviar Orden'}</span>
          </button>
        </form>
      </div>

      {/* Advertencias */}
      <div className="card bg-yellow-50 border border-yellow-200">
        <div className="flex items-start space-x-3">
          <Settings className="w-6 h-6 text-yellow-600 mt-1" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-2">
              Advertencias de Seguridad
            </h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Verifique que la planta esté en condiciones de recibir órdenes</li>
              <li>• Las órdenes se ejecutan inmediatamente</li>
              <li>• Asegúrese de tener autorización para enviar comandos</li>
              <li>• Todas las órdenes quedan registradas en el sistema</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrdenButton({ active, onClick, icon: Icon, title, description }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-4 rounded-lg border-2 transition-all text-left ${
        active
          ? 'border-primary-600 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <Icon className={`w-6 h-6 mb-2 ${active ? 'text-primary-600' : 'text-gray-600'}`} />
      <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
}

export default OrdenesPanel;
